import React, { useRef, useState, useEffect } from "react";
import RegionManagement from "../components/RegionManagement";
import PageImageManagement from "../components/PageImageManagement";
import { Tabs, Card, Row, Col, Statistic, Table, Button, Tag, Modal, Form, Input, Select, message, Popconfirm } from "antd";
import { UserOutlined, EnvironmentOutlined, HomeOutlined, PictureOutlined, PlusOutlined, TeamOutlined } from "@ant-design/icons";
import { getAdminUsers, updateUserStatus, updateUserRole, getAdminStatistics } from "../services/api";
import { PageLoadingOverlay } from "../components/RoomLoadingScene";

const adminDashboardCache = new Map();

const AdminDashboard = () => {
    const adminId = JSON.parse(localStorage.getItem("user") || "{}").id || "admin";
    const cachedAdminData = adminDashboardCache.get(adminId);
    const roleOptions = [
        { label: "普通用户", value: "user" },
        { label: "房东", value: "landlord" },
        { label: "中介", value: "agent" }
    ];
    const [stats, setStats] = useState(cachedAdminData?.stats || null);
    const [users, setUsers] = useState(cachedAdminData?.users || []);
    const [listedRooms, setListedRooms] = useState(cachedAdminData?.listedRooms || []);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(!cachedAdminData);
    const [userModal, setUserModal] = useState(false);
    const [creatingUser, setCreatingUser] = useState(false);
    const creatingUserRef = useRef(false);
    const [unlistModal, setUnlistModal] = useState(false);
    const [unlistReason, setUnlistReason] = useState("");
    const [selectedRoomId, setSelectedRoomId] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        const quiet = Boolean(cachedAdminData);
        Promise.all([loadStats(), loadUsers(quiet), loadListedRooms(quiet)]).finally(() => setInitialLoading(false));
    }, []);


    const loadStats = async () => { try { const res = await getAdminStatistics(); setStats(res); adminDashboardCache.set(adminId, { ...(adminDashboardCache.get(adminId) || {}), stats: res }); } catch (e) {} };
    const loadUsers = async (quiet = false) => { try { if (!quiet) setLoading(true); const res = await getAdminUsers(); const nextUsers = Array.isArray(res) ? res : []; setUsers(nextUsers); adminDashboardCache.set(adminId, { ...(adminDashboardCache.get(adminId) || {}), users: nextUsers }); } catch (e) {} finally { if (!quiet) setLoading(false); } };
    
    const loadListedRooms = async (quiet = false) => {
        try {
            if (!quiet) setLoading(true);
            const token = localStorage.getItem("token");
            const res = await fetch("/api/admin/rooms/listed", { headers: { Authorization: "Bearer " + token } });
            const data = await res.json();
            const nextRooms = Array.isArray(data) ? data : [];
            setListedRooms(nextRooms);
            adminDashboardCache.set(adminId, { ...(adminDashboardCache.get(adminId) || {}), listedRooms: nextRooms });
        } catch (e) {} finally { if (!quiet) setLoading(false); }
    };

    const handleToggleStatus = async (id, status) => {
        try { await updateUserStatus(id, status === "active" ? "banned" : "active"); message.success("操作成功"); loadUsers(); } catch (e) { message.error("操作失败"); }
    };

    const handleUpgradeRole = async (userId, newRole) => {
        try {
            await updateUserRole(userId, newRole);
            message.success("角色已更新"); 
            loadUsers(); 
            loadStats();
        } catch (e) { message.error("操作失败"); }
    };

    const handleCreateUser = async () => {
        if (creatingUserRef.current) return;
        creatingUserRef.current = true;
        setCreatingUser(true);
        try {
            const values = await form.validateFields();
            const token = localStorage.getItem("token");
            const res = await fetch("/api/admin/users", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                body: JSON.stringify(values)
            });
            const data = await res.json();
            if (res.ok) { message.success("账号创建成功，默认密码：" + values.password); setUserModal(false); form.resetFields(); loadUsers(); loadStats(); } else { message.error(data.error || "创建失败"); }
        } catch (e) {
            if (!e?.errorFields) message.error("创建失败");
        } finally {
            creatingUserRef.current = false;
            setCreatingUser(false);
        }
    };

    const handleUnlistRoom = (roomId) => {
        setSelectedRoomId(roomId);
        setUnlistReason("房源不合规管理员下架");
        setUnlistModal(true);
    };

    const confirmUnlist = async () => {
        try {
            const token = localStorage.getItem("token");
            await fetch("/api/admin/rooms/" + selectedRoomId + "/unlist", {
                method: "PUT",
                headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                body: JSON.stringify({ reason: unlistReason })
            });
            message.success("房源已下架");
            setUnlistModal(false);
            loadListedRooms();
            loadStats();
        } catch (e) { message.error("操作失败"); }
    };

    const mainUsers = (users || []).filter(u => u.role !== "sub_account");
    
    const getSubAccountCount = (parentId) => {
        return (users || []).filter(u => u.role === "sub_account" && u.parent_id === parentId).length;
    };

    const userColumns = [
        { title: "序号", key: "index", width: 60, render: (_, __, index) => index + 1 },
        { title: "用户名", dataIndex: "username", key: "username", render: (name, record) => (
            <div>
                <span>{name}</span>
                {record.role === "landlord" && getSubAccountCount(record.id) > 0 && (
                    <Tag color="blue" style={{ marginLeft: 8 }}><TeamOutlined /> {getSubAccountCount(record.id)}个子账号</Tag>
                )}
            </div>
        )},
        { title: "手机号", dataIndex: "phone", key: "phone" },
        { title: "角色", dataIndex: "role", key: "role", render: (r, record) => r === "admin"
            ? <Tag color="red">管理员</Tag>
            : <Select size="small" value={r} options={roleOptions} style={{ width: 110 }} onChange={value => handleUpgradeRole(record.id, value)} /> },
        { title: "状态", dataIndex: "status", key: "status", render: (s) => <Tag color={s === "active" ? "green" : "red"}>{s === "active" ? "正常" : "封禁"}</Tag> },
        { title: "操作", key: "action", render: (_, r) => {
            if (r.role === "admin") return null;
            return (
                <div style={{ display: "flex", gap: 8 }}>
                    <Popconfirm title="确定操作?" onConfirm={() => handleToggleStatus(r.id, r.status)}>
                        <Button size="small" type={r.status === "active" ? "danger" : "primary"}>{r.status === "active" ? "封禁" : "解封"}</Button>
                    </Popconfirm>
                </div>
            );
        }}
    ];

    const roomColumns = [
        { title: "序号", key: "index", width: 60, render: (_, __, index) => index + 1 },
        { title: "楼栋", dataIndex: "building_name", key: "building_name" },
        { title: "房号", dataIndex: "room_number", key: "room_number" },
        { title: "户型", key: "layout", render: (_, r) => r.bedrooms + "室" + (r.living_rooms || 0) + "厅" },
        { title: "租金", dataIndex: "rent", key: "rent", render: (v) => "¥" + v + "/月" },
        { title: "区域", dataIndex: "region_name", key: "region_name" },
        { title: "街道", dataIndex: "street_name", key: "street_name" },
        { title: "房东", dataIndex: "landlord_name", key: "landlord_name" },
        { title: "操作人", dataIndex: "listed_by_name", key: "listed_by_name" },
        { title: "状态", dataIndex: "status", key: "status", render: (s) => <Tag color={s === "vacant" ? "green" : "red"}>{s === "vacant" ? "空置" : "已租"}</Tag> },
        { title: "操作", key: "action", render: (_, r) => <Popconfirm title="确定要下架该房源吗？" onConfirm={() => handleUnlistRoom(r.id)}><Button size="small" danger>下架</Button></Popconfirm> }
    ];


    const tabItems = [
        {
            key: "users",
            label: <span><UserOutlined /> 用户管理</span>,
            children: (
                <div>
                    <div style={{ marginBottom: 16 }}>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setUserModal(true); }}>创建账号</Button>
                    </div>
                    <Table columns={userColumns} dataSource={mainUsers} rowKey="id" loading={loading} />
                </div>
            )
        },
        {
            key: "rooms",
            label: <span><HomeOutlined /> 房源管理</span>,
            children: (
                <div>
                    <h3>已上架房源管理</h3>
                    <Table columns={roomColumns} dataSource={listedRooms} rowKey="id" loading={loading} />
                </div>
            )
        },
        {
            key: "regions",
            label: <span><EnvironmentOutlined /> 区域管理</span>,
            children: <RegionManagement />
        },
        {
            key: "page-images",
            label: <span><PictureOutlined /> 页面图片</span>,
            children: <PageImageManagement />
        }
    ];

    return (
        <div>
            {initialLoading && <PageLoadingOverlay caption="正在加载管理数据，请稍候…" />}
            <div style={{ marginBottom: 24 }}>
                <Row gutter={[16, 16]}>
                    <Col xs={12} lg={6}><Card><Statistic title="总用户数" value={stats?.users?.total || 0} /></Card></Col>
                    <Col xs={12} lg={6}><Card><Statistic title="房东数量" value={stats?.users?.landlords || 0} valueStyle={{ color: "#1890ff" }} /></Card></Col>
                    <Col xs={12} lg={6}><Card><Statistic title="总房源数" value={stats?.rooms?.total || 0} /></Card></Col>
                    <Col xs={12} lg={6}><Card><Statistic title="已上架" value={stats?.rooms?.listed || 0} valueStyle={{ color: "#52c41a" }} /></Card></Col>
                </Row>
            </div>
            <Tabs items={tabItems} />
            <Modal title="创建账号" open={userModal} onOk={handleCreateUser} onCancel={() => { if (!creatingUser) setUserModal(false); }} confirmLoading={creatingUser} cancelButtonProps={{ disabled: creatingUser }}>
                <Form form={form} layout="vertical">
                    <Form.Item name="username" label="用户名" rules={[{ required: true, message: "请输入用户名" }]}><Input placeholder="例如：landlord2" /></Form.Item>
                    <Form.Item name="password" label="密码" rules={[{ required: true, message: "请输入密码" }, { min: 6, message: "密码至少6位" }]}><Input.Password placeholder="至少6位" /></Form.Item>
                    <Form.Item name="phone" label="手机号"><Input placeholder="选填" /></Form.Item>
                    <Form.Item name="role" label="角色" rules={[{ required: true, message: "请选择角色" }]}>
                        <Select placeholder="请选择角色">
                            <Select.Option value="user">普通用户</Select.Option>
                            <Select.Option value="landlord">房东</Select.Option>
                            <Select.Option value="agent">中介</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
            <Modal title="下架房源" open={unlistModal} onOk={confirmUnlist} onCancel={() => setUnlistModal(false)} okText="确认下架" okButtonProps={{ danger: true }}>
                <Form layout="vertical">
                    <Form.Item label="下架原因">
                        <Input.TextArea rows={4} value={unlistReason} onChange={(e) => setUnlistReason(e.target.value)} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default AdminDashboard;





